import moment from "moment";
import Swal from "sweetalert2";

import { isArray } from "lodash";

import Constants from "constants/index";
import Strings from "constants/strings";
import { IMultiLanguageContent } from "./interfaces";

/**
 * Helpers.ts
 *
 * Common function for app.
 */
const Helpers = {
    /**
     * Check value is string or non.
     *
     * @param {any} value: The value to be tested.
     * @returns {boolean} If data type is string true. Otherwise it returns false.
     */
    isString: (value: any): value is string => {
        return typeof value === "string";
    },

    /**
     * Check value is object or non.
     *
     * @param {any} value: The value to be tested.
     * @returns {boolean} If data type is object true. Otherwise it returns false.
     */
    isObject: (value: any): value is object => {
        return typeof value === "object";
    },

    /**
     * Determine if the argument passed is a JavaScript function object.
     *
     * @param {any} obj: Object to test whether or not it is an array.
     * @returns {boolean} returns a Boolean indicating whether the object is a JavaScript function
     */
    isFunction: (value: any): value is (...args: any) => void => {
        return typeof value === "function";
    },

    /**
     * Check a value is number or non, if number then return true, otherwise return false.
     *
     * @param {string} value: Value can check number
     * @returns {boolean} if number then return true, otherwise return false.
     */
    isNumber: (value: any): value is number => {
        return typeof value === "number";
    },

    /**
     * Check a value is NaN or not
     *
     * @param {any} value: Value can check number
     * @returns {boolean} if NaN then return true, otherwise return false.
     */
    isNaN: (value: any): value is number => {
        return isNaN(value);
    },

    /**
     * Check Object is null or String null or empty.
     *
     * @param {object | string} value Object or String
     * @returns {boolean} if null or empty return true, otherwise return false.
     */
    isNullOrEmpty: (value: any): value is undefined | boolean | null => {
        if (isArray(value) && value.length === 0) {
            return true;
        } else return value === undefined || value === null || value === "";
    },

    isValidPhoneNumber: (phoneNumber: string) => {
        return Constants.RegExp.PHONE_NUMBER.test(phoneNumber);
    },

    isValidEmail: (email: string) => {
        return Constants.RegExp.NEW_EMAIL_ADDRESS.test(email);
    },

    isValidIdCard: (idCard: string) => {
        return Constants.RegExp.ID_CARD.test(idCard);
    },

    isCocCoc: () => {
        const thisWindow: any = window;
        const brands: any[] = thisWindow?.navigator?.userAgentData?.brands || [];
        const indexOfCocCoc = brands?.findIndex((item) => item.brand === Constants.COCCOC_BRAND_NAME);
        return indexOfCocCoc !== -1;
    },

    /**
     * Detect running inside Zalo in-app browser (WebView).
     * Zalo appends "Zalo" to the user-agent string.
     *
     * @returns {boolean} true if current browser agent is Zalo.
     */
    isZalo: (userAgent?: string): boolean => {
        const ua = userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
        return /Zalo/i.test(ua);
    },

    /**
     * Get iOS major version from the user-agent (e.g. 17 for "CPU iPhone OS 17_1").
     *
     * @returns {number | null} Major version, or null when not iOS / not detectable.
     */
    getIOSVersion: (userAgent?: string): number | null => {
        const ua = userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
        if (!/iPhone|iPad|iPod/i.test(ua)) {
            return null;
        }

        const match = ua.match(/OS (\d+)[._]/i);
        return match ? parseInt(match[1], 10) : null;
    },

    /**
     * Trim space character (start, end) of input string.
     *
     * @param {string} value: Value for trim
     * @returns {string} String after trim, space start & end is removed
     */
    trim: (value: string): string => {
        return Helpers.isString(value) ? value.trim() : "";
    },

    /**
     * If value is string return value, otherwise return value.toString
     *
     * @param {string} value: Value
     * @returns {string} String or convert of value to string
     */
    ensureString: (value: any): string => {
        try {
            if (!Helpers.isNullOrEmpty(value)) {
                if (Helpers.isString(value)) {
                    return value;
                } else if (Helpers.isObject(value)) {
                    return JSON.stringify(value);
                } else {
                    return `${value}`;
                }
            }
        } catch (error) {
            return "";
        }
        return "";
    },

    /**
     * Convert size in bytes to KB, MB, GB or TB
     *
     * @param {number} bytes: Size convert
     * @returns {string} Value formatted include unit.
     */
    bytesToSize: (bytes: number): string => {
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
        if (Helpers.isNullOrEmpty(bytes) || bytes === 0) {
            return "0 Byte";
        }
        const i = Math.floor(Math.floor(Math.log(bytes) / Math.log(1024)));
        return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`;
    },

    /**
     * Convert date to string with custom format.
     *
     * @param {number | Date} date Date or Timestamp
     * @param {string} format Format string output
     */
    dateToString: (date: number | Date | undefined, format?: string): string => {
        if (Helpers.isNullOrEmpty(date)) {
            return "";
        } else if (date instanceof Date && date.getTime() === 0) {
            return "";
        } else if ((typeof date === "number" && date === 0) || (typeof date === "string" && date === "0")) {
            return "";
        } else if (Helpers.isNumber(date) && `${date}`.length === 10) {
            return moment.unix(date).format(format || Constants.DateFormat.DDMMYYYY);
        } else {
            return moment(date).format(format || Constants.DateFormat.DDMMYYYY);
        }
    },

    toUnix: (value?: any) => {
        if (Helpers.isNullOrEmpty(value) || value === 0 || value === "0") {
            return undefined;
        } else if (`${value}`.length === 10) {
            return Number(value) * 1000;
        } else {
            return moment(value).unix();
        }
    },

    fromNow: (date: number | Date): string => {
        return moment(date).fromNow();
    },

    /**
     * Convert string to date.
     *
     * @param {string} dateString string
     */
    stringToDate: (dateString: string): Date => {
        return new Date(dateString);
    },

    /**
     * Convert string to hex color code.
     *
     * @param {string} inputString string
     */
    stringToColor: (inputString: string) => {
        let hash = 0;
        for (let i = 0; i < inputString.length; i++) {
            hash = inputString.charCodeAt(i) + ((hash << 5) - hash);
        }

        let color = "#";
        for (let i = 0; i < 3; i++) {
            const value = (hash >> (i * 8)) & 0xff;
            color += ("00" + value.toString(16)).substr(-2);
        }

        return color;
    },

    /**
     * Get protocal from url.
     * e.g. URL is https://google.com, protocal output is [https:]
     *
     * @param {string} url URL
     * @returns {string} Protocal of URL, if not a URL return empty string
     */
    getProtocolFromURL: (url: string): string => {
        const urlTrim = Helpers.trim(url);
        const index = urlTrim.indexOf("//");
        if (index > -1) {
            return urlTrim.substring(0, index);
        }
        return "";
    },

    /**
     * Format numbers with leading zeros
     *
     * @param {number} num A number
     * @param {number} size Sring output length
     * @returns {string} String format with leading zero
     */
    zeroPad: (num: number, size: number): string => {
        let result = `${num}`;
        while (result.length < size) {
            result = "0" + result;
        }
        return result;
    },

    /**
     * Copy object properties to another object
     *
     * @param {any} sourceObj Object
     * @param {any} distObj Object
     */
    copyProperties: (sourceObj: any, distObj: any) => {
        for (const key in sourceObj) {
            if (!sourceObj.hasOwnProperty(key)) {
                continue;
            }
            const sourceObjData: any = sourceObj[key];
            if (!Helpers.isNullOrEmpty(sourceObjData)) {
                if (Array.isArray(sourceObjData)) {
                    const distObjData: any = [];
                    Helpers.copyProperties(sourceObjData, distObjData);
                    distObj[key] = distObjData;
                    continue;
                }
                if (Helpers.isObject(sourceObjData)) {
                    const distObjData: any = {};
                    Helpers.copyProperties(sourceObjData, distObjData);
                    distObj[key] = distObjData;
                    continue;
                }
            }
            distObj[key] = sourceObjData;
        }
    },

    /**
     * Clone object
     *
     * @param {T} sourceObj Object
     */
    cloneObject: <T>(sourceObj: T): T => {
        const cloneObj: T = {} as T;
        Helpers.copyProperties(sourceObj, cloneObj);
        return cloneObj;
    },

    /**
     * Show alert
     *
     * @param {string} message message for display
     * @param {"warning" | "success" | "error" | "info" | undefined} type type of alert
     */
    showAlert: async (message: string, type?: "warning" | "success" | "error" | "info" | "question", okCallback?: any) => {
        const msg = message;
        const okPress = await Swal.fire({
            customClass: {
                container: "custom-sweetalert2",
                confirmButton: "custom-sweetalert2-btn-ok",
            },
            text: msg,
            icon: type || "warning",
            allowOutsideClick: false,
        });
        if (okPress && okPress.isConfirmed && okCallback && Helpers.isFunction(okCallback)) {
            okCallback();
        }
    },

    /**
     * Show confirm alert
     *
     * @param {string} message message for display
     * @param {function} okCallback callback handle when click ok
     * @param {function} cancelCallback callback handle when click cancel
     */

    showConfirmAlert: async (message: string, okCallback: any, cancelCallback?: any, okButtonMessage?: string, cancelButtonMessage?: string) => {
        const msg = message;
        const okPress = await Swal.fire({
            customClass: {
                container: "custom-sweetalert2",
                confirmButton: "custom-sweetalert2-btn-ok",
                cancelButton: "custom-sweetalert2-btn-cancel",
            },
            text: msg,
            icon: "warning",
            reverseButtons: true,
            showCancelButton: true,
            confirmButtonText: okButtonMessage || Strings.COMMON.OK,
            cancelButtonText: cancelButtonMessage || Strings.COMMON.CANCEL,
        });
        if (okPress && okPress.isConfirmed && okCallback && Helpers.isFunction(okCallback)) {
            okCallback();
        } else {
            if (cancelCallback && Helpers.isFunction(cancelCallback)) {
                cancelCallback();
            }
        }
    },

    getFileExtesion: (fullFileName?: string) => {
        if (!Helpers.isNullOrEmpty(fullFileName)) {
            const length = fullFileName.length;
            const extension = fullFileName.slice(fullFileName.lastIndexOf(".") + 1, length);
            return extension.toLowerCase();
        } else {
            return "";
        }
    },

    readFileAsArrayBuffer: (file: File) => {
        return new Promise((resolve, reject) => {
            let reader = new FileReader();

            reader.onerror = (event) => {
                reject(event);
            };

            reader.onload = function (event) {
                const arrayBuffer = new Uint8Array(reader.result as any);
                resolve(arrayBuffer);
            };

            reader.readAsArrayBuffer(file);
        });
    },

    handleFormatParams(data: any) {
        const params = new URLSearchParams();
        Object.entries(data).forEach(([key, values]) => {
            if (!Helpers.isNullOrEmpty(values)) {
                if (Array.isArray(values)) {
                    if (values.length > 0) {
                        values.forEach((value, index) => {
                            if (!Helpers.isNullOrEmpty(value) && typeof value !== "object") {
                                params.append(key, value.toString());
                            }
                            if (!Helpers.isNullOrEmpty(value) && typeof value === "object") {
                                Object.entries(value).forEach(([objkey, objValue]) => {
                                    if (!Helpers.isNullOrEmpty(objValue)) params.append(`${key}[${index}].${objkey}`, (objValue || "").toString());
                                });
                            }
                        });
                    }
                } else {
                    params.append(key, `${values}`);
                }
            }
        });
        return params.toString();
    },

    joinUrl(base: string, ...paths: string[]) {
        let normalizedBase = base;
        if (!base.match(/^https?:\/\//)) {
            normalizedBase = "https://" + base.replace(/^\/+/, "");
        }

        const url = new URL(normalizedBase);
        for (const path of paths) {
            url.pathname = new URL(path, url).pathname;
        }
        return url.toString();
    },

    handleFormatJSON: (data: any) => {
        const result: any = {};
        Object.entries(data).forEach(([key, values]) => {
            if (!Helpers.isNullOrEmpty(values)) {
                result[key] = values;
            }
        });
        return result;
    },

    formatCurrency: (number: number | string, decimals?: number) => {
        var a = Number(number)
            .toFixed(decimals || 0)
            .split(".");
        a[0] = a[0].replace(/\d(?=(\d{3})+$)/g, "$&,");
        if (Number(number) < 0) {
            return `${a.join(".")}`;
        } else {
            return a.join(".");
        }
    },

    setItemInSessionStorage: (storageKey: string, value: any) => {
        sessionStorage.setItem(storageKey, JSON.stringify(value));
    },

    getItemInSessionStorage: (storageKey: string, defaultValue?: any): any => {
        const value = sessionStorage.getItem(storageKey);
        if (!Helpers.isNullOrEmpty(value) && value !== "undefined" && value !== "null") {
            return JSON.parse(value);
        } else {
            return defaultValue;
        }
    },

    setItemInLocalStorage: (storageKey: string, value: any) => {
        localStorage.setItem(storageKey, JSON.stringify(value));
    },

    getItemInLocalStorage: (storageKey: string, defaultValue?: any): any => {
        const value = localStorage.getItem(storageKey);
        if (!Helpers.isNullOrEmpty(value) && value !== "undefined" && value !== "null") {
            return JSON.parse(value);
        } else {
            return defaultValue;
        }
    },

    converStringToJson: (value?: any): any => {
        if (Helpers.isNullOrEmpty(value) || value === "undefined" || value === "null") {
            return undefined;
        } else {
            try {
                const newValue = JSON.parse(value);
                return newValue;
            } catch (error) {
                return undefined;
            }
        }
    },

    getUrlParams: (keys: string[]): { [key: string]: string | undefined } => {
        const params = new URLSearchParams(window.location.search);
        let datas: { [key: string]: string | undefined } = {};
        keys.forEach((key) => {
            datas[key] = params.get(key) || undefined;
        });
        return datas;
    },

    handleExceptionError: (error?: any, isEvent?: boolean) => {
        if (Number(error?.code) === Constants.ApiCode.NOT_PERMISSION) {
            if (isEvent) {
                __EventEmitter.emit(Constants.EventName.NOT_DATA_PERMISSION);
            } else {
                return error?.message;
            }
        } else if (Number(error?.code) === Constants.ApiCode.NOT_FOUND) {
            if (isEvent) {
                __EventEmitter.emit(Constants.EventName.NOT_FOUND);
            } else {
                return error?.message;
            }
        } else {
            if (Helpers.isString(error)) {
                return error;
            } else {
                let mesErr: string = "";
                let validationErr: string = "";

                if (
                    error?.message?.responseException?.validationErrors &&
                    !Helpers.isNullOrEmpty(error?.message?.responseException?.validationErrors)
                ) {
                    const arr: string[] = [];
                    for (const element of [...error?.message?.responseException?.validationErrors || []]) {
                        element.message && arr.push(element.message)
                    };

                    validationErr = arr.join(";\n");
                    if (arr.length > 1) {
                        validationErr += ";";
                    }
                };

                if (!Helpers.isNullOrEmpty(error?.Message)) {
                    mesErr = error?.Message;
                }
                if (!Helpers.isNullOrEmpty(error?.message)) {
                    mesErr = error?.message;
                }
                if (!Helpers.isNullOrEmpty(error?.message?.message)) {
                    mesErr = error?.message?.message;
                }
                if (!Helpers.isNullOrEmpty(error?.message?.Message)) {
                    mesErr = error?.message?.Message;
                }
                if (!Helpers.isNullOrEmpty(error?.message?.responseException?.exceptionMessage)) {
                    mesErr = error?.message?.responseException?.exceptionMessage;
                }
                if (!Helpers.isNullOrEmpty(error?.message?.responseException?.ExceptionMessage)) {
                    mesErr = error?.message?.responseException?.ExceptionMessage;
                }
                if (!Helpers.isNullOrEmpty(error?.message?.ResponseException?.exceptionMessage)) {
                    mesErr = error?.message?.ResponseException?.exceptionMessage;
                }
                if (!Helpers.isNullOrEmpty(error?.message?.ResponseException?.ExceptionMessage)) {
                    mesErr = error?.message?.ResponseException?.ExceptionMessage;
                }

                if (!Helpers.isNullOrEmpty(error?.responseException?.exceptionMessage)) {
                    mesErr = error?.responseException?.exceptionMessage;
                }
                if (!Helpers.isNullOrEmpty(error?.responseException?.ExceptionMessage)) {
                    mesErr = error?.responseException?.ExceptionMessage;
                }
                if (!Helpers.isNullOrEmpty(error?.ResponseException?.exceptionMessage)) {
                    mesErr = error?.ResponseException?.exceptionMessage;
                }
                if (!Helpers.isNullOrEmpty(error?.ResponseException?.ExceptionMessage)) {
                    mesErr = error?.ResponseException?.ExceptionMessage;
                }

                return validationErr || mesErr || Strings.MESSAGE.COMMON_ERROR;
            }
        }
    },

    getDefaultValueMultiLanguage: (data: IMultiLanguageContent | undefined, currentLanguage?: string, allowOtherLanguage?: boolean): string => {
        if (Helpers.isNullOrEmpty(data)) {
            return "";
        } else {
            if (Helpers.isNullOrEmpty(currentLanguage)) {
                if (Helpers.isNullOrEmpty(data?.value?.[Constants.DefaultLanguage])) {
                    if (allowOtherLanguage) {
                        const value: any = [...Object.values(data?.value) || []].find(v => !Helpers.isNullOrEmpty(v));
                        return value || "";
                    } else {
                        return "";
                    }
                } else {
                    return data?.value?.[Constants.DefaultLanguage] || "";
                }
            } else {
                if (Helpers.isNullOrEmpty(data?.value?.[currentLanguage])) {
                    if (Helpers.isNullOrEmpty(data?.value?.[Constants.DefaultLanguage])) {
                        if (allowOtherLanguage) {
                            const value: any = [...Object.values(data?.value) || []].find(v => !Helpers.isNullOrEmpty(v));
                            return value || "";
                        } else {
                            return "";
                        }
                    } else {
                        return data?.value?.[Constants.DefaultLanguage] || "";
                    }
                } else {
                    return data?.value?.[currentLanguage] || "";
                }
            }
        };
    },

    getPageNumber: (pageNumber?: number, pageSize?: number, totalCount?: number) => {
        const size = pageSize || 1;
        const page = pageNumber || 1;
        const total = totalCount || 0;
        const newPage = (Math.ceil(total / size) || 1);
        return (page > newPage) ? newPage : page;
    },

    uniqueArray: (arr: (string | number)[]) => {
        return arr.reduce((unique, item) => unique.includes(item) ? unique : [...unique, item], []);
    },

    isDev: (): boolean  => {
        return process.env.NODE_ENV !== "production";
    }
};

export default Helpers;
